import {
  CittadinoServizioDTOTipoServizioEnum,
  TipologiaPratica,
} from '../../core/api/generated';
import {ServizioCapability} from '../enums/servizio-capability.enum';

/**
 * Tabella-ponte FE dei "servizi logici" offerti al cittadino.
 *
 * NON esiste (ancora) un endpoint cittadino "servizi di un comune": il BE modella
 * `Comune ──< Servizio ──< TipoPratica ──< Pratica` ma lo espone al cittadino solo
 * come tipologie di bando. Questa config FE raggruppa le `TipologiaPratica` per
 * COMPORTAMENTO (non 1:1), dichiarando per ciascun servizio le sezioni (`capability`)
 * e l'insieme di tipologie che vi rientrano (usato dai blocchi della Home; "Le mie pratiche" non filtra).
 *
 * DEBITO ESPLICITO: la FORMA di questa tabella coincide con il futuro contratto BE
 * `/cittadino/comuni/{slug}/servizi`. Quando arriverà, si sostituisce SOLO la sorgente
 * (config → API) dentro CatalogoServiziCittadinoService, senza toccare forma né consumatori
 * (switcher, menu data-driven, Home). Vedi design.md §2-§3.
 *
 * Analogo a `TipologiaPraticaMeta`: config FE che arricchisce un enum BE.
 */
export type ServizioSlug = 'mensa' | 'agevolazioni';

export interface ServizioLogico {
  /** Slug stabile: entra nell'URL `/c/{comuneSlug}/{servizioSlug}/...`. Non cambiarlo. */
  slug: ServizioSlug;
  /**
   * Chiave Transloco del nome del servizio (root scope `common.servizio.*.label`), NON il
   * testo. Costante statica valutata all'import: se contenesse il nome localizzato, questo
   * resterebbe congelato all'italiano anche in EN — e verrebbe interpolato così com'è nelle
   * frasi già tradotte (es. `home.mensa.attiva` → "Your Mensa scolastica is active").
   * Risolvere al punto d'uso.
   */
  labelKey: string;
  icona: string;
  /** Colore-identità del servizio (ponte transizione). Mai unico veicolo: sempre + icona + label. */
  coloreIdentita: string;
  /**
   * Descrizione breve del servizio (sentence case), usata nella faccia "scopri" della Home
   * quando il cittadino non ha ancora pratiche (home-servizio-dashboard §9). Campo statico
   * (estensione della tabella-ponte): quando arriverà l'endpoint BB servizi, arriverà da lì.
   */
  descrizioneKey: string;
  /** Quali TipologiaPratica rientrano nel servizio. Alimenta anche il filtro pratiche. */
  tipologie: TipologiaPratica[];
  /**
   * Quali `tipo_servizio` BE (dall'endpoint `GET /api/v1/cittadino/servizi`) rientrano in
   * questo servizio-logico. Gemello di `tipologie[]` ma sull'asse ACCESSI (servizi sbloccati
   * da pratiche accettate), non sull'asse catalogo. Punto UNICO di estensione: aggiungere un
   * futuro tipo BE = aggiungere il suo enum qui. Vuoto = il servizio non ha runtime BE
   * (es. "agevolazioni": oggi nessun `tipo_servizio` corrisponde).
   */
  tipiBE: CittadinoServizioDTOTipoServizioEnum[];
  /** Quali sezioni il servizio espone. Alimenta il menu data-driven. Ordine non significativo. */
  capability: ServizioCapability[];
}

/**
 * I due servizi-logici (design.md §2). Sorgente unica e isolata: i consumatori NON
 * leggono mai questa costante direttamente — passano da CatalogoServiziCittadinoService.
 */
export const SERVIZI_LOGICI: ServizioLogico[] = [
  {
    slug: 'mensa',
    labelKey: 'common.servizio.mensa.label',
    icona: 'restaurant-outline',
    coloreIdentita: '#0F5C9C',
    descrizioneKey: 'common.servizio.mensa.descrizione',
    tipologie: [TipologiaPratica.MENSA],
    tipiBE: [CittadinoServizioDTOTipoServizioEnum.MENSA],
    capability: [
      ServizioCapability.BANDI,
      ServizioCapability.PRATICHE,
      ServizioCapability.VOUCHER,
      ServizioCapability.PAGAMENTI,
      ServizioCapability.FIGLI,
      ServizioCapability.MENU,
      ServizioCapability.CALENDARIO,
      ServizioCapability.TARIFFE,
    ],
  },
  {
    slug: 'agevolazioni',
    labelKey: 'common.servizio.agevolazioni.label',
    icona: 'ribbon-outline',
    coloreIdentita: '#7A5A00',
    descrizioneKey: 'common.servizio.agevolazioni.descrizione',
    tipologie: [
      TipologiaPratica.BUONI_LIBRO,
      TipologiaPratica.BUONI_SPESA,
      TipologiaPratica.AGEVOLAZIONE,
      TipologiaPratica.PACCO_ALIMENTARE,
      TipologiaPratica.CEDOLE_LIBRARIE,
    ],
    tipiBE: [],
    capability: [ServizioCapability.BANDI, ServizioCapability.PRATICHE, ServizioCapability.VOUCHER],
  },
];

/** Lookup per slug (consumato da resolver/ripristino servizio dall'URL). */
export function getServizioLogicoBySlug(slug: string | null | undefined): ServizioLogico | null {
  if (!slug) return null;
  return SERVIZI_LOGICI.find((s) => s.slug === slug) ?? null;
}

/** Servizio-logico a cui appartiene una tipologia di bando (usato dal filtro pratiche). */
export function getServizioLogicoByTipologia(
  tipologia: TipologiaPratica | null | undefined
): ServizioLogico | null {
  if (!tipologia) return null;
  return SERVIZI_LOGICI.find((s) => s.tipologie.includes(tipologia)) ?? null;
}

/**
 * Classifica un `tipo_servizio` BE (asse ACCESSI) nel suo ServizioLogico.
 * Unico punto che traduce l'enum BE in categoria FE: un tipo non mappato → null
 * (la frontiera lo scarta, non inventa una categoria). Vedi servizi-attivi-cittadino.
 */
export function getServizioLogicoByTipoBE(
  tipoBE: CittadinoServizioDTOTipoServizioEnum | null | undefined
): ServizioLogico | null {
  if (!tipoBE) return null;
  return SERVIZI_LOGICI.find((s) => s.tipiBE.includes(tipoBE)) ?? null;
}
